import React, {useEffect, useState} from "react"
import {useNavigate} from "react-router-dom"
// MUI
import {Box, Button, Skeleton, Typography} from "@mui/material";
import {Add, InfoOutlined, PhoneAndroidRounded} from "@mui/icons-material";
// Data
import {useUser} from "@/context/UserProvider";
import {useData} from "@/context/PackagingDataProvider";
// Components
import {AssociatedBrandsDialog} from "@/components/UI";
import {PageHeader} from "@/components/Layout";
import AppCard from "@/components/Apps/AppCard";
import AppPreviewDialog from "@/components/Apps/AppPreviewDialog/AppPreviewDialog";
import DeleteAppDialog from "@/components/Apps/DeleteAppDialog/DeleteAppDialog";
import {packagingTokens as tk} from "@/theme/packagingTokens";
// Translation
import {useTranslation} from "react-i18next";
import {useModule} from "@/context/ModuleProvider";
import {usePermissions} from "@/context/PermissionsProvider";

export default function AppsTab() {

    const {t} = useTranslation(["apps", "common"])
    const navigate = useNavigate()
    const {organisation} = useUser()
    const {apps, getApps, createAny} = useData()
    const {modules} = useModule()
    const {isAccountRole} = usePermissions()

    const lockEdition = isAccountRole(["analyst"])
    const showUnavailable = !!modules && modules.gs1 && modules.geoloc

    const [sortedApps, setSortedApps] = useState<any[]>([])
    const [openBrandsDialog, setOpenBrandsDialog] = useState<string | boolean>(false)
    const [previewApp, setPreviewApp] = useState<any>(null)
    const [deletionApp, setDeletionApp] = useState<any>(null)

    useEffect(() => {
        if (!!organisation && !apps) getApps()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organisation])

    useEffect(() => {
        if (!!apps && !!apps.length) {
            let _apps = [...apps]
            _apps.sort((a: any, b: any) => Number(new Date(b.created_at)) - Number(new Date(a.created_at)))
            _apps.sort((app: any) => app.default ? -1 : 1)
            setSortedApps(_apps)
        } else {
            setSortedApps([])
        }
    }, [apps])

    const handleEdit = (app: any) => navigate("/apps/edit", {state: {...app}})

    return (
        <Box>
            <PageHeader
                title={t("apps")}
                subtitle={t("apps_subtitle", {defaultValue: "Geolocation landing apps for your brands"})}
                actions={
                    <Button
                        variant={"contained"} disableElevation
                        startIcon={<Add sx={{fontSize: 16}} />}
                        onClick={() => createAny({}, "app")}
                        disabled={lockEdition}
                        sx={{textTransform: "none", fontSize: "13.5px", fontWeight: 600, borderRadius: tk.radius.sm, px: 2}}
                    >
                        {t("app_create")}
                    </Button>
                }
            />

            {showUnavailable && (
                <Box
                    sx={{
                        display: "flex", alignItems: "center", gap: 1, mb: 2.5, px: 2, py: 1.5,
                        borderRadius: tk.radius.md, backgroundColor: tk.color.blue100,
                        border: `1px solid ${tk.color.blue}33`,
                    }}
                >
                    <InfoOutlined sx={{fontSize: 18, color: tk.color.blue}} />
                    <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                        {t("apps_unavailable_for_gs1")}
                    </Typography>
                </Box>
            )}

            {apps === null || apps === undefined ? (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)"}, gap: 2}}>
                    {Array.from({length: 3}).map((_, i) => (
                        <Skeleton key={i} variant={"rounded"} height={220} sx={{borderRadius: tk.radius.card}} />
                    ))}
                </Box>
            ) : sortedApps.length === 0 ? (
                <Box sx={{textAlign: "center", py: 8, px: 3, border: `1px dashed ${tk.color.border}`, borderRadius: tk.radius.card, backgroundColor: tk.color.surface}}>
                    <PhoneAndroidRounded sx={{fontSize: 32, color: tk.color.textTertiary, mb: 1}} />
                    <Typography sx={{fontSize: "15px", fontWeight: 700, mb: 0.5}}>{t("no_elements_found")}</Typography>
                    <Typography sx={{fontSize: "13px", color: tk.color.textSecondary}}>
                        {t("apps_empty_hint", {defaultValue: "Create an app to add a geolocation landing for your brands."})}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)"}, gap: 2}}>
                    {sortedApps.map((app: any) => (
                        <AppCard
                            key={app.id}
                            app={app}
                            disableActions={lockEdition}
                            onEdit={handleEdit}
                            onAssociatedBrands={(a) => setOpenBrandsDialog(a.id)}
                            onPreview={(a) => setPreviewApp(a)}
                            onDelete={(a) => setDeletionApp(a)}
                            t={t}
                        />
                    ))}
                </Box>
            )}

            <AssociatedBrandsDialog
                appUuid={openBrandsDialog}
                open={Boolean(openBrandsDialog)}
                onClose={() => {
                    setOpenBrandsDialog(false)
                    getApps()
                }}
                {...{t}}
            />

            <AppPreviewDialog
                open={Boolean(previewApp)}
                onClose={() => setPreviewApp(null)}
                app={previewApp}
                {...{t}}
            />

            <DeleteAppDialog
                open={Boolean(deletionApp)}
                onClose={() => setDeletionApp(null)}
                app={deletionApp}
                apps={sortedApps}
                onDeleted={() => getApps()}
                {...{t}}
            />
        </Box>
    )
}
